import { useExpertUniversityConfiguration, useExpertUniversityStore } from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import { showNotification } from "@mantine/notifications";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLogger } from "@/sdk/logging/LoggerProvider";

import type {
    EvaluationResultsErrorResponse,
    SessionEvaluationSkippedStatus,
    TranscriptContent,
} from "../api/evaluations/types";

import { evaluateTrainingSessionBySessionId, getEvaluationResultsBySessionId } from "../api/requests";
import { TrainingSessionChallengeResultStatus, TrainingSessionChallengeStatusEnum } from "../types";
import { isEvaluationTranscriptContent } from "../utils";

// Statuses the `session/{sessionId}/evaluate` endpoint returns when there is nothing to render: the
// evaluation was already saved, or the session was ineligible. These carry no EvaluationDetails, so we
// simply clear the loading state and stay on the learning-blocks view (no results page, no session save).
const SKIPPED_STATUSES: readonly SessionEvaluationSkippedStatus[] = ["already_completed", "too_short", "no_transcript"];

const isSkippedStatus = (status: string): status is SessionEvaluationSkippedStatus =>
    (SKIPPED_STATUSES as readonly string[]).includes(status);

const EVALUATION_ERROR_RESPONSE: EvaluationResultsErrorResponse = Object.freeze({
    ErrorCode: "EVALUATION_ERROR",
    Message: "Failed to fetch evaluation results",
});

const MAX_RETRIES = 3;

const isRetryableError = (result: { status: string; statusCode?: number; error?: string }) =>
    (result.status === "server_error" && result.statusCode !== undefined && result.statusCode >= 500) ||
    (result.status === "error" && result.error?.includes("Failed to fetch"));

/**
 * Discriminates *why* the hook is in a pending state so consumers can render different interim UI:
 * - `"evaluating"`: the post-call `trigger-evaluation-results` flow — a fresh evaluation is being
 *   generated right after a call ends (`session/{sessionId}/evaluate`).
 * - `"loading"`: the `viewResultsBySessionId` flow — reading a previously-stored result.
 */
export type EvaluationPendingReason = "evaluating" | "loading";

export interface UseTrainingEvaluationResults {
    evaluationResults: TranscriptContent | EvaluationResultsErrorResponse | undefined;
    /**
     * Backward-compatible boolean pending flag, derived as `pendingReason !== undefined`. True while
     * either the post-call evaluation or the stored-result read is in flight.
     */
    isPending: boolean;
    /**
     * Why the hook is pending, or `undefined` when idle. The UI renders a "call complete" interstitial
     * for `"evaluating"` (a fresh evaluation is being generated after a call ends) but a plain loading
     * state for `"loading"` (reading a previously-stored result).
     */
    pendingReason: EvaluationPendingReason | undefined;
    hasRetryableError: boolean;
    retryEvaluation: () => void;
    clearEvaluationResults: () => void;
    /**
     * Loads the already-stored evaluation for a past session (the tile's "View results" click) and
     * drives the same loading/results/error state as the live post-call flow. This is distinct from
     * the `session/{sessionId}/evaluate` endpoint the mount-time listener uses: that generates a fresh
     * evaluation on hangup, whereas this reads a persisted result and never re-triggers evaluation.
     */
    viewResultsBySessionId: (sessionId: string) => Promise<void>;
}

/**
 * Owns the ASCEND-pilot evaluation-results flow: it listens for the `trigger-evaluation-results`
 * event, calls the new `session/{sessionId}/evaluate` endpoint, and drives the loading/results/error
 * state that `ExpertUniversityLearningBlocks` renders. Mirrors the FullStory + logger events in
 * `ExpertUniversityHome` on a completed evaluation, but against the new endpoint. The endpoint already
 * persists the evaluation to the `ai-assisted-training` DS record server-side, so this hook no longer
 * writes the training session from the client. Because only the ASCEND experience mounts
 * `ExpertUniversityLearningBlocks`, wiring this hook there inherently scopes the behavior to the pilot.
 */
export function useTrainingEvaluationResults(): UseTrainingEvaluationResults {
    const logger = useLogger({ module: "Expert University Learning Blocks" });
    const { taskInSandbox } = useExpertUniversityConfiguration();

    const [evaluationResults, setEvaluationResults] = useState<
        TranscriptContent | EvaluationResultsErrorResponse | undefined
    >();
    // Seed from the store's `isEvaluationPending` flag, which the SDK sets in `hangupCall` before the
    // task-completion that remounts this component. Seeding lazily means the interstitial is on screen
    // on the very first render — no flash of the default learning-blocks view while we wait for the
    // `trigger-evaluation-results` event. Sandbox 'calls' produce no evaluation, so never seed there.
    const [pendingReason, setPendingReason] = useState<EvaluationPendingReason | undefined>(() =>
        !taskInSandbox && useExpertUniversityStore.getState().isEvaluationPending ? "evaluating" : undefined,
    );
    const [hasRetryableError, setHasRetryableError] = useState(false);

    // The subscription effect runs once (empty deps), so read the latest sandbox flag through a ref
    // rather than capturing the mount-time value in the async handler's closure.
    const taskInSandboxRef = useRef(taskInSandbox);
    taskInSandboxRef.current = taskInSandbox;

    const retryCountRef = useRef(0);
    const lastEventRef = useRef<{ sessionId: string; profileId: string } | undefined>(undefined);

    async function processEvaluation(sessionId: string, profileId: string) {
        try {
            const result = await evaluateTrainingSessionBySessionId(sessionId);

            // Success: a completed evaluation carries the TranscriptContent payload (has
            // EvaluationDetails, so it satisfies isEvaluationTranscriptContent). The status
            // discriminant narrows the union to the completed shape first so the guard type-checks.
            if (result.status === "completed" && isEvaluationTranscriptContent(result)) {
                // The freshly-triggered evaluation response may omit ChallengeId. Fall back to the
                // profileId from the event so the updated-call-flow checklist resolves correctly.
                result.ChallengeId ??= profileId;

                const evaluationOutcome = result.EvaluationDetails.Results?.toLocaleLowerCase();
                const challengeStatus =
                    evaluationOutcome === TrainingSessionChallengeResultStatus.CLEARED
                        ? TrainingSessionChallengeStatusEnum.COMPLETE
                        : TrainingSessionChallengeStatusEnum.NOT_COMPLETE;

                // The new endpoint's EvaluationDetails omits the `logs` block, so behaviorsPresent /
                // maxBehaviors are unavailable here and are intentionally not reported.
                trackFullStoryEvent("Evaluation Results Received", {
                    sessionId,
                    profileId,
                    evaluationResults: evaluationOutcome,
                });
                logger.info(
                    {
                        challengeStatus,
                        evaluationResults: evaluationOutcome,
                        sessionId,
                        profileId,
                        isAscend: true,
                    },
                    "Triggering evaluation results",
                );

                setEvaluationResults(result);
                setPendingReason(undefined);
                return;
            }

            // already_completed: the evaluation was persisted server-side but the original response
            // never reached the client (e.g. a 5xx on the gateway after the backend committed). Fetch
            // the stored result so the expert sees their score instead of a "no results" toast.
            if (result.status === "already_completed") {
                logger.info({ sessionId, isAscend: true }, "Evaluation already completed; fetching stored result");
                const stored = await getEvaluationResultsBySessionId(sessionId);
                const storedResult = stored?.items?.[0];
                if (storedResult && isEvaluationTranscriptContent(storedResult)) {
                    storedResult.ChallengeId ??= profileId;
                    trackFullStoryEvent("Evaluation Results Received", {
                        sessionId,
                        profileId,
                        evaluationResults: storedResult.EvaluationDetails?.Results?.toLocaleLowerCase(),
                    });
                    setEvaluationResults(storedResult);
                    setPendingReason(undefined);
                    return;
                }
                logger.error({ sessionId, isAscend: true }, "already_completed but no stored result found");
                trackFullStoryEvent("Evaluation Results Error", { sessionId, profileId });
                setEvaluationResults(EVALUATION_ERROR_RESPONSE);
                setPendingReason(undefined);
                return;
            }

            // Skipped: no evaluation payload. Clear loading and stay on the learning-blocks view.
            if (isSkippedStatus(result.status)) {
                logger.info(
                    { status: result.status, sessionId, isAscend: true },
                    "Evaluation skipped; no results to display",
                );
                showNotification({
                    id: "evaluation-skipped",
                    position: "top-center",
                    withCloseButton: true,
                    autoClose: 4000,
                    message: "No evaluation results are available for this session.",
                });
                setEvaluationResults(undefined);
                setPendingReason(undefined);
                return;
            }

            // Everything else funnels here (not_found / server_error / error, or a completed
            // response that unexpectedly lacked its payload). Surface the error overlay.

            if (isRetryableError(result) && retryCountRef.current < MAX_RETRIES) {
                retryCountRef.current += 1;
                setHasRetryableError(true);
                logger.warn(
                    {
                        retryCount: retryCountRef.current,
                        sessionId,
                        isAscend: true,
                    },
                    "Retrying evaluation results",
                );
                return;
            }
            logger.error(
                {
                    error: "error" in result ? result.error : undefined,
                    status: result.status,
                    sessionId,
                    isAscend: true,
                },
                "Error triggering evaluation results",
            );
            trackFullStoryEvent("Evaluation Results Error", {
                sessionId,
                profileId,
            });

            setEvaluationResults(EVALUATION_ERROR_RESPONSE);
            setPendingReason(undefined);
        } catch (error: any) {
            logger.error({ error: error?.message, sessionId, isAscend: true }, "Error triggering evaluation results");
            trackFullStoryEvent("Evaluation Results Error", {
                sessionId,
                profileId,
            });
            setEvaluationResults(EVALUATION_ERROR_RESPONSE);
            setPendingReason(undefined);
        }
    }

    useEffect(() => {
        // Consume the seed flag once: we've already read it into `pendingReason` above, so clear it here
        // so a later remount (without a fresh call) doesn't re-open the interstitial. The live flow from
        // here on is driven entirely by the `trigger-evaluation-results` event and local state.
        if (useExpertUniversityStore.getState().isEvaluationPending) {
            useExpertUniversityStore.getState().setEvaluationPending(false);
        }

        const eventBus = useExpertUniversityStore.getState().eventBus;
        const off = eventBus?.on("trigger-evaluation-results", async (event) => {
            // After ending a 'call' in the sandbox environment there are no evaluation results to fetch.
            if (taskInSandboxRef.current) {
                setEvaluationResults(undefined);
                return;
            }
            lastEventRef.current = { sessionId: event.sessionId, profileId: event.profileId };
            retryCountRef.current = 0;
            setHasRetryableError(false);
            setPendingReason("evaluating");

            await processEvaluation(event.sessionId, event.profileId);
        });

        return () => {
            off?.();
        };
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    const retryEvaluation = useCallback(() => {
        const event = lastEventRef.current;
        if (!event) return;
        setHasRetryableError(false);
        setPendingReason("evaluating");
        void processEvaluation(event.sessionId, event.profileId);
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    const clearEvaluationResults = useCallback(() => {
        setEvaluationResults(undefined);
    }, []);

    const viewResultsBySessionId = useCallback(
        async (sessionId: string) => {
            setPendingReason("loading");
            try {
                const response = await getEvaluationResultsBySessionId(sessionId);
                const result = response?.items?.[0];
                if (result) {
                    trackFullStoryEvent("Evaluation Results Received", { sessionId });
                    setEvaluationResults(result);
                } else {
                    // The read endpoint swallows its own errors (returns undefined) and may also come
                    // back empty; either way there is nothing to render, so surface the error overlay.
                    logger.info({ sessionId, isAscend: true }, "No stored evaluation results found for session");
                    trackFullStoryEvent("Evaluation Results Error", { sessionId });
                    setEvaluationResults(EVALUATION_ERROR_RESPONSE);
                }
            } catch (error: any) {
                // Defensive: the read endpoint swallows its own errors today, but a malformed response
                // (e.g. missing `items`) would otherwise reject a discarded promise silently. Surface the
                // error overlay and keep the failure observable, mirroring the post-call flow above.
                logger.error(
                    { error: error?.message, sessionId, isAscend: true },
                    "Failed to load stored evaluation results",
                );
                trackFullStoryEvent("Evaluation Results Error", { sessionId });
                setEvaluationResults(EVALUATION_ERROR_RESPONSE);
            } finally {
                setPendingReason(undefined);
            }
        },
        [logger],
    );

    const isPending = pendingReason !== undefined;

    return {
        evaluationResults,
        isPending,
        pendingReason,
        hasRetryableError,
        retryEvaluation,
        clearEvaluationResults,
        viewResultsBySessionId,
    };
}
